import React, { useState, useRef, useCallback, useEffect } from "react";
import { brandsService } from "../../services/brands";
import { trackEvent } from "../../utils/tracking";
import OrbitalLoader from "../common/OrbitalLoader";
import { FIELD_CONFIGS } from "../../utils/brands";
import { getApiErrorMessage } from "../../utils/apiErrors";

const CheckIcon = () => (
  <svg
    className="w-4 h-4 shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const Spinner = () => <OrbitalLoader size={14} className="inline-flex shrink-0" />;

export default function BrandCreateForm({
  projectHash,
  onCreated,
  onCancel,
  title = "Create New Brand",
  subtitle = "Add brand details and we'll curate content ideas to your voice and style.",
  className = "",
  cancelLabel = "Cancel",
  createLabel = "Create Brand",
  showHeader = true,
}) {
  const [fields, setFields] = useState(() => {
    const init = {};
    FIELD_CONFIGS.forEach((config) => {
      init[config.key] = { value: "", status: "idle", detail: null, error: null };
    });
    return init;
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const validationNonce = useRef({});
  const debounceTimers = useRef({});
  const statusRef = useRef({});

  FIELD_CONFIGS.forEach((config) => {
    if (!(config.key in statusRef.current)) statusRef.current[config.key] = "idle";
  });

  useEffect(() => {
    const timersRef = debounceTimers.current;
    return () => {
      Object.values(timersRef).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const updateField = useCallback((key, updates) => {
    if ("status" in updates) statusRef.current[key] = updates.status;
    setFields((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  }, []);

  const runValidation = useCallback(
    (key, rawValue) => {
      if (statusRef.current[key] !== "idle") return;
      const raw = (rawValue || "").trim();
      if (!raw) return;
      const config = FIELD_CONFIGS.find((item) => item.key === key);
      const parsed = config?.parse(raw);
      if (!parsed || !config) return;

      const nonce = Symbol();
      validationNonce.current[key] = nonce;
      updateField(key, { status: "validating" });

      const isStale = () => validationNonce.current[key] !== nonce;

      if (config.type === "url") {
        brandsService.validateInputs(
          { url: parsed },
          (response) => {
            if (isStale()) return;
            const result = response.data?.url;
            if (result?.valid) {
              updateField(key, {
                status: "valid",
                detail: { url: parsed, title: result?.title },
                error: null,
              });
              return;
            }
            updateField(key, {
              status: "error",
              error: result?.error || "Could not reach this URL.",
            });
          },
          () => {
            if (isStale()) return;
            updateField(key, { status: "error", error: "Validation failed." });
          }
        );
        return;
      }

      brandsService.validateInputs(
        { creators: [{ platform: key, handle: parsed }] },
        (response) => {
          if (isStale()) return;
          const result = response.data?.creators?.[0];
          if (result?.valid) {
            updateField(key, {
              status: "valid",
              detail: { handle: parsed, name: result.name, warning: result.warning },
              error: null,
            });
            return;
          }
          updateField(key, {
            status: "error",
            error: result?.error || `Can't find this ${config.label} channel.`,
          });
        },
        () => {
          if (isStale()) return;
          updateField(key, { status: "error", error: "Validation failed." });
        }
      );
    },
    [updateField]
  );

  const handleChange = useCallback(
    (key, value) => {
      validationNonce.current[key] = null;
      clearTimeout(debounceTimers.current[key]);
      statusRef.current[key] = "idle";
      updateField(key, { value, status: "idle", detail: null, error: null });

      if (value.trim()) {
        debounceTimers.current[key] = setTimeout(() => {
          if (statusRef.current[key] === "idle") {
            runValidation(key, value);
          }
        }, 800);
      }
    },
    [runValidation, updateField]
  );

  const handleBlur = useCallback(
    (key, value) => {
      clearTimeout(debounceTimers.current[key]);
      if ((value || "").trim() && statusRef.current[key] === "idle") {
        runValidation(key, value);
      }
    },
    [runValidation]
  );

  const handleKeyDown = useCallback(
    (key, value, event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        clearTimeout(debounceTimers.current[key]);
        if ((value || "").trim() && statusRef.current[key] === "idle") {
          runValidation(key, value);
        }
      }
    },
    [runValidation]
  );

  const hasAnyValid = Object.values(fields).some((field) => field.status === "valid");
  const hasAnyValidating = Object.values(fields).some((field) => field.status === "validating");
  const canSubmit = hasAnyValid && !hasAnyValidating && Boolean(projectHash);

  const handleCreate = () => {
    if (!canSubmit) return;
    setError(null);
    setCreating(true);

    const creators = [];
    FIELD_CONFIGS.forEach((config) => {
      if (config.type !== "creator") return;
      const field = fields[config.key];
      if (field.status !== "valid") return;
      creators.push({ platform: config.key, handle: field.detail.handle });
    });

    const websiteField = fields.website;
    const payload = {
      project_hash: projectHash,
      domain: websiteField.status === "valid" ? websiteField.detail.url : undefined,
      creators: creators.length ? creators : undefined,
    };

    brandsService.createBrand(
      payload,
      (response) => {
        setCreating(false);
        const brand = response?.data;
        trackEvent('create_brand', { brand_hash: brand?.hash, project_hash: projectHash });
        onCreated?.(brand);
      },
      (requestError) => {
        setCreating(false);
        setError(getApiErrorMessage(requestError, "Failed to create brand. Please try again."));
      }
    );
  };

  return (
    <div className={className}>
      {showHeader ? (
        <>
          <h2 className="text-2xl font-semibold text-theme-text mb-1">{title}</h2>
          <p className="text-sm text-theme-text opacity-70 mb-6">{subtitle}</p>
        </>
      ) : null}

      {error ? (
        <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded text-sm text-theme-text">
          {error}
        </div>
      ) : null}

      <div className="space-y-4 mb-6">
        {FIELD_CONFIGS.map((config) => {
          const field = fields[config.key];
          return (
            <div key={config.key}>
              <label className="block text-sm font-medium text-theme-text mb-1">{config.label}</label>
              <input
                type="text"
                value={field.value}
                onChange={(event) => handleChange(config.key, event.target.value)}
                onBlur={(event) => handleBlur(config.key, event.target.value)}
                onKeyDown={(event) => handleKeyDown(config.key, field.value, event)}
                placeholder={config.placeholder}
                className={`w-full px-3 py-2 rounded border bg-theme-main text-theme-text placeholder-theme-text placeholder-opacity-40 transition-colors ${
                  field.status === "valid"
                    ? "border-green-500/50"
                    : field.status === "error"
                      ? "border-red-500/50"
                      : "border-theme-secondary"
                }`}
              />
              {field.status === "validating" ? (
                <p className="mt-1.5 text-xs text-theme-text opacity-60 flex items-center gap-1.5">
                  <Spinner />
                  Checking...
                </p>
              ) : null}
              {field.status === "valid" && config.type === "url" ? (
                <p className="mt-1.5 text-xs text-green-500 font-semibold flex items-center gap-1.5">
                  <CheckIcon />
                  {field.detail?.title || "Reachable"}
                </p>
              ) : null}
              {field.status === "valid" && config.type === "creator" ? (
                <p className="mt-1.5 text-xs text-green-500 font-semibold flex items-center gap-1.5">
                  <CheckIcon />
                  <span>@{field.detail?.handle}</span>
                  {field.detail?.name && field.detail.name !== field.detail.handle ? (
                    <span>- {field.detail.name}</span>
                  ) : null}
                </p>
              ) : null}
              {field.status === "valid" && field.detail?.warning ? (
                <p className="mt-1.5 text-xs text-amber-500 font-medium">{field.detail.warning}</p>
              ) : null}
              {field.status === "error" ? (
                <p className="mt-1.5 text-xs text-red-500 font-semibold">{field.error}</p>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-3">
          {onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-theme-secondary rounded-md text-theme-text hover:bg-theme-secondary hover:opacity-50"
            >
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canSubmit || creating}
            className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? "Creating..." : createLabel}
          </button>
      </div>
    </div>
  );
}
